import { useEffect, useState } from 'react';
import { call, type MediaStatus } from './api';

/**
 * Wat er in Seerr al is aangevraagd, gedeeld door alle posters: na "Aanvragen" staat er
 * overal meteen "Aangevraagd", ook in rijen die nog uit de cache komen.
 */

const known = new Map<string, MediaStatus>();
const releases = new Map<string, string | null>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function setRequestStatus(type: 'movie' | 'tv', tmdbId: number, status: MediaStatus, release?: string | null): void {
  known.set(`${type}:${tmdbId}`, status);
  if (release !== undefined) releases.set(`${type}:${tmdbId}`, release);
  emit();
}

/** Verwachte releasedatum van een aangevraagde titel die nog niet uit is. */
export function releaseOf(type: 'movie' | 'tv', tmdbId: number): string | null | undefined {
  return releases.get(`${type}:${tmdbId}`);
}

/** "vandaag", "morgen", "over 12 dagen" (tot een maand), anders "14 nov" (met jaar als het volgend jaar is). */
export function releaseLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((date.getTime() - today.getTime()) / 86_400_000);
  if (days <= 0) return 'vandaag';
  if (days === 1) return 'morgen';
  if (days <= 30) return `over ${days} dagen`;
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) }).replace('.', '');
}

/** Label voor een aangevraagde titel: wordt hij al gedownload of komt hij nog uit? */
export function requestLabels(status: MediaStatus | undefined, release: string | null | undefined): { main: string; date?: string; icon?: 'calendar' | 'bolt' } | null {
  if (!status || status === 'none') return null;
  if ((status === 'requested' || status === 'processing') && release === 'pending') return { main: '✓ Aangevraagd', date: 'Zodra digitaal uit', icon: 'bolt' };
  if ((status === 'requested' || status === 'processing') && release) return { main: '✓ Aangevraagd', date: releaseLabel(release), icon: 'calendar' };
  const main = REQUEST_LABEL[status];
  return main ? { main } : null;
}

export function requestStatusOf(type: 'movie' | 'tv', tmdbId: number): MediaStatus | undefined {
  return known.get(`${type}:${tmdbId}`);
}

/** Herrendert bij elke wijziging. */
export function useRequestStatuses(): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    const l = () => setV((x) => x + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return v;
}

/** Status van een reeks titels bij Seerr ophalen (één verzoek) en onthouden. */
export async function loadRequestStatuses(items: { type: 'movie' | 'tv'; tmdbId: number }[]): Promise<void> {
  const todo = items.filter((i) => !known.has(`${i.type}:${i.tmdbId}`));
  if (!todo.length) return;
  const r = await call<{ status: Record<string, MediaStatus>; release?: Record<string, string | null> }>('/api/seerr/status-of', { method: 'POST', body: JSON.stringify({ items: todo }) }).catch(() => null);
  if (!r) return;
  for (const [k, v] of Object.entries(r.status)) known.set(k, v);
  for (const [k, v] of Object.entries(r.release ?? {})) releases.set(k, v);
  emit();
}

export const REQUEST_LABEL: Partial<Record<MediaStatus, string>> = {
  requested: '✓ Aangevraagd',
  processing: '⏳ Wordt gedownload',
  partial: 'Deels binnen',
  available: '✓ Beschikbaar',
};
